import { useClerk } from '@clerk/react';
import { Loader, Text } from '@mantine/core';
import { Button } from '@vrplatform/components';
import { getOwnerPortalReturnUrl, useOwnerPortal } from '@vrplatform/data';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { EmptyViewWrapper } from '../../components';

/**
 * Signed-in hand-off on the primary domain. An invitation visit ends the
 * current session and returns to the exact invitation URL so the ticket is
 * accepted by the invited identity; a completed token verification returns to
 * the registered satellite origin.
 */
export const OwnerPortalTransition = () => {
  const portal = useOwnerPortal();
  if (!portal) throw new Error('Owner portal branding is required');
  const location = useLocation();
  const { signOut } = useClerk();
  const [failed, setFailed] = useState(false);
  const isInvitation = location.pathname === '/invitation';
  const returnUrl = isInvitation
    ? new URL(
        `${location.pathname}${location.search}${location.hash}`,
        window.location.origin
      ).href
    : getOwnerPortalReturnUrl(
        new URLSearchParams(location.search).get('redirect_url'),
        portal.hostname
      );
  useEffect(() => {
    if (isInvitation) {
      // Ticket acceptance must use the invited identity, including when the
      // current session belongs to an owner of another team or an unlinked user.
      void signOut({ redirectUrl: returnUrl }).catch(() => setFailed(true));
    } else {
      window.location.replace(returnUrl);
    }
  }, [isInvitation, returnUrl, signOut]);
  return (
    <OwnerPortalTransitionView
      portalName={portal.name}
      isInvitation={isInvitation}
      failed={failed}
    />
  );
};

export const OwnerPortalTransitionView = ({
  portalName,
  isInvitation,
  failed,
}: {
  portalName: string;
  isInvitation: boolean;
  failed: boolean;
}) => {
  if (failed)
    return (
      <EmptyViewWrapper
        title="Unable to prepare invitation"
        description="We couldn't sign out of the current account. Try again; your invitation link is preserved."
        actions={
          <Button variant="primary" onClick={() => window.location.reload()}>
            Try again
          </Button>
        }
      />
    );
  return (
    <EmptyViewWrapper>
      <Loader type="dots" size="md" />
      <Text c="gray" ta="center" mt="lg" size="lg">
        {isInvitation
          ? 'Preparing your invitation...'
          : `Returning to ${portalName}...`}
      </Text>
    </EmptyViewWrapper>
  );
};
